import * as Dialog from '@radix-ui/react-dialog';
import { isObject } from 'lodash';
import { Children, type PropsWithChildren, type ReactNode } from 'react';

import { cnTw, nonNullable } from '@/shared/lib/utils';
import { IconButton } from '@/shared/ui/Buttons/IconButton/IconButton';
import { ScrollArea } from '../ScrollArea/ScrollArea';
import { useTheme } from '../Theme/useTheme';

import './Modal.css';

type Props = {
  isOpen?: boolean;
  size: 'sm' | 'md' | 'mdlg' | 'lg' | 'xl' | 'xxl' | 'full' | 'fit';
  height?: 'full' | 'lg' | 'fit';
  testId?: string;
  preventOutsideClick?: boolean;
  onToggle?: (open: boolean) => void;
};

const Root = ({
  isOpen,
  size = 'md',
  height = 'fit',
  children,
  onToggle,
  testId = 'Modal',
  preventOutsideClick = false,
}: PropsWithChildren<Props>) => {
  const { portalContainer } = useTheme();

  const arrayChildren = Children.toArray(children);
  const triggerNode = arrayChildren.find(child => {
    return nonNullable(child) && isObject(child) && 'type' in child && child.type === Trigger;
  });
  const modalNodes = triggerNode ? arrayChildren.filter(child => child !== triggerNode) : arrayChildren;

  const hasTitle =
    modalNodes.find(child => {
      return nonNullable(child) && isObject(child) && 'type' in child && child.type === Title;
    }) !== null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={onToggle}>
      {triggerNode}
      <Dialog.Portal container={portalContainer}>
        <Dialog.Overlay
          className={cnTw(
            'absolute inset-0 z-50 flex min-h-full items-center justify-center overflow-hidden p-4',
            'bg-dim-background',
            'duration-300 animate-in fade-in',
          )}
        >
          <Dialog.Content
            aria-describedby={undefined}
            data-testid={testId}
            className={cnTw(
              'ui-kit-modal-height flex max-w-full min-w-32 flex-col overflow-hidden',
              'text-left align-middle text-body',
              'transform rounded-lg bg-white shadow-modal transition-transform',
              'duration-200 animate-in fade-in zoom-in-95',
              {
                'w-modal-sm': size === 'sm',
                'w-modal': size === 'md',
                'w-148': size === 'mdlg',
                'w-modal-lg': size === 'lg',
                'w-modal-xl': size === 'xl',
                'w-full': size === 'full',
                'w-fit': size === 'fit',
                'h-fit': height === 'fit',
                'h-full': height === 'full',
                'h-modal': height === 'lg',
              },
            )}
            onInteractOutside={preventOutsideClick ? e => e.preventDefault() : undefined}
          >
            {hasTitle ? null : <Dialog.Title hidden />}
            {modalNodes}
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

type TitleProps = PropsWithChildren<{
  action?: ReactNode;
  close?: boolean;
  gap?: 'none' | 'small' | 'medium' | 'large';
}>;

const Title = ({ action, close, children, gap = 'medium' }: TitleProps) => {
  const headerExist = nonNullable(children) || nonNullable(action) || nonNullable(close);

  return (
    <Dialog.Title asChild hidden={!headerExist} className={!headerExist ? 'hidden' : ''}>
      <header className="flex w-full items-center justify-between py-3 ps-5 pe-3 contain-inline-size">
        <h1 className="w-full truncate py-1 font-manrope text-header-title font-bold text-text-primary">{children}</h1>

        <div
          className={cnTw('z-20 flex h-7.5 items-center', {
            'gap-x-0': gap === 'none',
            'gap-x-2': gap === 'small',
            'gap-x-4': gap === 'medium',
            'gap-x-6': gap === 'large',
          })}
        >
          {action}

          {close && (
            <Dialog.Close asChild>
              <IconButton name="close" size={20} className="m-1" />
            </Dialog.Close>
          )}
        </div>
      </header>
    </Dialog.Title>
  );
};

type HeaderContentProps = PropsWithChildren<{
  background?: 'primary' | 'secondary';
}>;

const HeaderContent = ({ children, background = 'primary' }: HeaderContentProps) => {
  return (
    <header
      aria-level={2}
      className={cnTw('shrink-0', {
        'bg-main-app-background': background === 'secondary',
      })}
    >
      {children}
    </header>
  );
};

type ContentProps = PropsWithChildren<{
  disableScroll?: boolean;
  background?: 'primary' | 'secondary';
}>;

const Content = ({ disableScroll, background = 'primary', children }: ContentProps) => {
  return (
    <div
      className={cnTw('relative flex h-full min-h-0 grow flex-col overflow-hidden', {
        'bg-main-app-background': background === 'secondary',
      })}
    >
      {disableScroll ? children : <ScrollArea>{children}</ScrollArea>}
    </div>
  );
};

const Trigger = ({ disabled, children }: PropsWithChildren<{ disabled?: boolean }>) => {
  return (
    <Dialog.Trigger disabled={disabled} asChild>
      {children}
    </Dialog.Trigger>
  );
};

const Footer = ({ children, align = 'end' }: PropsWithChildren<{ align?: 'start' | 'end' | 'between' }>) => {
  return (
    <footer
      className={cnTw('flex h-fit items-center gap-2 px-5 pt-3 pb-4', {
        'justify-end': align === 'end',
        'justify-start': align === 'start',
        'justify-between': align === 'between',
      })}
    >
      {children}
    </footer>
  );
};

export const Modal = Object.assign(Root, {
  Trigger,
  Title,
  HeaderContent,
  Content,
  Footer,
});
